import { useState, useCallback } from 'react';
import { sanitizeInput, sanitizePhone, sanitizeEmail } from '../utils/sanitizer';

/**
 * Custom hook for sanitized input handling
 * @param {string} initialValue - Initial input value
 * @param {Object} options - Sanitization options
 * @returns {Object} - Input state and handlers
 */
export function useSanitizedInput(initialValue = '', options = {}) {
  const {
    type = 'strict',
    inputType = 'text', // 'text', 'phone', 'email'
    maxLength = null,
    onSanitize = null
  } = options;

  const [value, setValue] = useState(initialValue);
  const [sanitizedValue, setSanitizedValue] = useState(initialValue);

  const handleChange = useCallback((event) => {
    const newValue = event.target.value;
    
    // Apply length limit if specified
    const limitedValue = maxLength ? newValue.substring(0, maxLength) : newValue;
    
    // Sanitize based on input type
    let sanitized;
    switch (inputType) {
      case 'phone':
        sanitized = sanitizePhone(limitedValue) || limitedValue;
        break;
      case 'email':
        sanitized = sanitizeEmail(limitedValue) || limitedValue;
        break;
      default:
        sanitized = sanitizeInput(limitedValue, type);
    }
    
    setValue(limitedValue);
    setSanitizedValue(sanitized);
    
    // Call custom sanitization callback if provided
    if (onSanitize) {
      onSanitize(limitedValue, sanitized);
    }
  }, [type, inputType, maxLength, onSanitize]);

  const handleBlur = useCallback(() => {
    // On blur, update the value to the sanitized version
    setValue(sanitizedValue);
  }, [sanitizedValue]);

  const reset = useCallback(() => {
    setValue(initialValue);
    setSanitizedValue(initialValue);
  }, [initialValue]);

  return {
    value,
    sanitizedValue,
    handleChange,
    handleBlur,
    reset,
    isDirty: value !== sanitizedValue
  };
}

/**
 * Custom hook for sanitized form data
 * @param {Object} initialData - Initial form data
 * @param {string} formType - Type of form for sanitization config
 * @returns {Object} - Form state and handlers
 */
export function useSanitizedForm(initialData = {}, formType = 'default') {
  const [formData, setFormData] = useState(initialData);
  const [sanitizedData, setSanitizedData] = useState(initialData);

  const updateField = useCallback((fieldName, value, fieldType = 'strict') => {
    const sanitized = sanitizeInput(value, fieldType);
    
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    setSanitizedData(prev => ({
      ...prev,
      [fieldName]: sanitized
    }));
  }, []);

  const updateFormData = useCallback((newData) => {
    setFormData(newData);
    setSanitizedData(newData);
  }, []);

  const reset = useCallback(() => {
    setFormData(initialData);
    setSanitizedData(initialData);
  }, [initialData]);

  const getSanitizedData = useCallback(() => {
    return sanitizedData;
  }, [sanitizedData]);

  return {
    formData,
    sanitizedData,
    updateField,
    updateFormData,
    reset,
    getSanitizedData
  };
}

const sanitizedInputHooks = {
  useSanitizedInput,
  useSanitizedForm
};

export default sanitizedInputHooks;
